'use client';

import cx from 'classnames';

import { type Masked, unmask } from '~/contentful/graphql';
import { Typename } from '~/contentful/graphql/typename';
import { CardSize } from '~/ui/components/cards/base';
import { Column, Grid, type GridProps } from '~/ui/components/grid';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { CallForConcepts } from '~/ui/modules/call-for-concepts';
import { AnnualReportCard } from '~/ui/modules/cards/annualReport';
import { ArticleCard } from '~/ui/modules/cards/article';
import { FeatureCard } from '~/ui/modules/cards/feature';
import { FutureEventCard } from '~/ui/modules/cards/futureEvent';
import { GrantStoryCard } from '~/ui/modules/cards/grantStory';
import { IdeaCard } from '~/ui/modules/cards/idea';
import { MmufCard } from '~/ui/modules/cards/mmuf';
import { QuoteModule } from '~/ui/modules/quote';
import { Breakpoint } from '~/ui/styles/grid';
import gridStyles from '~/ui/styles/grid.module.scss';
import typographyStyles from '~/ui/styles/typography.module.scss';

import styles from './content-grid.module.scss';
import { type ContentGrid_EntryFragment } from './query';

type ContentItem = Masked<typeof ContentGrid_EntryFragment>;

export interface ContentGridProps extends GridProps {
  title?: string;
  items: (ContentItem | React.ReactNode)[];
}

export function ContentGrid({ title, items, ...props }: ContentGridProps) {
  const isSmallBreakpoint = useBreakpoint(Breakpoint.MOBILE);

  return (
    <Grid {...props}>
      {title && (
        <Column
          span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 5, [Breakpoint.DESKTOP]: 5 }}
          offsetLeft={{ [Breakpoint.DESKTOP]: 1 }}
        >
          <h2 className={cx(typographyStyles.modulesTitle, styles.title)}>{title}</h2>
        </Column>
      )}
      {/* Emphasize card if its the only one in the item set */}
      {items.length === 1 && isCanBeEmphasized(items[0]) ? (
        <Column
          span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 7, [Breakpoint.DESKTOP]: 7 }}
          offsetLeft={{ [Breakpoint.DESKTOP]: 1 }}
        >
          {renderItem(items[0], true)}
        </Column>
      ) : (
        <>
          <Column
            span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 3, [Breakpoint.DESKTOP]: 3 }}
            offsetLeft={{ [Breakpoint.DESKTOP]: 1 }}
          >
            <Grid>
              {items.map((item, index) =>
                isSmallBreakpoint ? (
                  isContentItem(item) ? (
                    <Column
                      span={{ [Breakpoint.MOBILE]: 4 }}
                      offsetLeft={{ [Breakpoint.MOBILE]: index % 2 === 0 ? 0 : 1 }}
                      offsetRight={{ [Breakpoint.MOBILE]: index % 2 === 0 ? 1 : 0 }}
                      className={styles.cardWrapper}
                      key={index}
                    >
                      <div
                        className={cx(styles.cardInnerWrapper, {
                          [styles.cardInnerWrapperOdd]: index % 2 === 0,
                        })}
                      >
                        {renderItem(item)}
                      </div>
                    </Column>
                  ) : (
                    <Column
                      span={{ [Breakpoint.MOBILE]: 5 }}
                      className={styles.cardWrapper}
                      key={index}
                    >
                      <div className={gridStyles.overflowLeft} style={{ marginTop: '2.4rem' }}>
                        {item}
                      </div>
                    </Column>
                  )
                ) : index % 2 === 0 ? (
                  isContentItem(item) ? (
                    <Column
                      span={{ [Breakpoint.MOBILE]: 4 }}
                      offsetLeft={{ [Breakpoint.MOBILE]: index % 2 === 0 ? 0 : 1 }}
                      offsetRight={{ [Breakpoint.MOBILE]: index % 2 === 0 ? 1 : 0 }}
                      className={styles.cardWrapper}
                      key={index}
                    >
                      <div
                        className={cx(styles.cardInnerWrapper, {
                          [styles.cardInnerWrapperOdd]: index % 2 === 0,
                        })}
                      >
                        {renderItem(item)}
                      </div>
                    </Column>
                  ) : (
                    <Column
                      span={{ [Breakpoint.MOBILE]: 5 }}
                      className={styles.cardWrapper}
                      key={index}
                    >
                      <div className={gridStyles.overflowLeft} style={{ marginTop: '2.4rem' }}>
                        {item}
                      </div>
                    </Column>
                  )
                ) : null,
              )}
            </Grid>
          </Column>

          {/* render odd cards on right side for medium and > breakpoints */}
          {!isSmallBreakpoint && (
            <Column
              span={{ [Breakpoint.TABLET]: 3, [Breakpoint.DESKTOP]: 4 }}
              offsetLeft={{ [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }}
            >
              <Grid>
                {items
                  .filter((_, index) => index % 2 !== 0)
                  .map((item, index) =>
                    isContentItem(item) ? (
                      <Column
                        span={{ [Breakpoint.TABLET]: 3, [Breakpoint.DESKTOP]: 3 }}
                        className={styles.cardWrapperOdd}
                        key={index}
                      >
                        {renderItem(item)}
                      </Column>
                    ) : (
                      <Column
                        span={{ [Breakpoint.TABLET]: 4, [Breakpoint.DESKTOP]: 4 }}
                        className={styles.cardWrapperOdd}
                        key={index}
                      >
                        {item}
                      </Column>
                    ),
                  )}
              </Grid>
            </Column>
          )}
        </>
      )}
    </Grid>
  );
}

function isContentItem(item: ContentGridProps['items'][number]): item is ContentItem {
  return !!item && typeof item === 'object' && '__typename' in item;
}
function isCanBeEmphasized(item: ContentGridProps['items'][number]): item is ContentItem {
  return (
    isContentItem(item) &&
    '__typename' in item &&
    (item.__typename === Typename.ARTICLE_PAGE || item.__typename === Typename.GRANT_STORY)
  );
}
function renderItem(data: ContentItem, isEmphasized?: boolean) {
  const item = unmask<typeof ContentGrid_EntryFragment>(data);

  switch (item.__typename) {
    case Typename.ARTICLE_PAGE:
      return (
        <ArticleCard
          data={item}
          className={isEmphasized ? styles.emphasizedCard : undefined}
          size={isEmphasized ? CardSize.EMPHASIZED : undefined}
        />
      );

    case Typename.GRANT_STORY:
      return (
        <GrantStoryCard
          data={item}
          className={isEmphasized ? styles.emphasizedCard : undefined}
          size={isEmphasized ? CardSize.EMPHASIZED : undefined}
        />
      );

    case Typename.IDEA_PAGE:
      return <IdeaCard data={item} />;

    case Typename.QUOTE:
      return <QuoteModule quote={item} isNarrow />;

    case Typename.EVENT_UPCOMING:
      return <FutureEventCard data={item} />;

    case Typename.FEATURE:
      return <FeatureCard data={item} />;

    case Typename.MMUF_PAGE:
      return <MmufCard data={item} />;

    case Typename.ANNUAL_REPORT_PAGE:
      return <AnnualReportCard data={item} size={CardSize.DEFAULT} />;

    case Typename.CALL_FOR_CONCEPT:
      return <CallForConcepts data={item} />;
  }
}
